<script setup>
import { computed, onMounted, ref } from "vue";
import apiClient from "@/apiClient";
import FinanceBarChart from "@/components/FinanceBarChart.vue";
import FinanceLineChart from "@/components/FinanceLineChart.vue";
import FinanceNetLineChart from "@/components/FinanceNetLineChart.vue";
import dayjs from "dayjs";

const loading = ref(false);
const error = ref("");
const financeSummary = ref({
  monthly_totals: [],
  sales_by_month: [],
  year_over_year: [],
  cash_conversion_gap: [],
  upcoming_due: {
    start_date: null,
    end_date: null,
    days: 30,
    count: 0,
    total: 0,
    jobs: [],
  },
});
const bankSummary = ref({
  monthly_in: [],
  monthly_out: [],
  year_over_year_out: [],
});

const monthlyComparisonBars = computed(() =>
  (financeSummary.value.monthly_totals || []).slice(-12),
);
const monthlySalesBars = computed(() =>
  (financeSummary.value.sales_by_month || []).slice(-12),
);

const financeYearlyBars = computed(() =>
  (financeSummary.value.year_over_year || []).map((entry) => ({
    label: entry.label,
    total: entry.total ?? 0,
  })),
);
const cashConversionSeries = computed(() => financeSummary.value.cash_conversion_gap || []);
const cashConversionLabels = computed(() =>
  cashConversionSeries.value.map((entry) => entry.label),
);
const cashConversionValues = computed(() =>
  cashConversionSeries.value.map((entry) => entry.total ?? 0),
);
const cashConversionTooltipRows = (tooltipItems) => {
  const index = tooltipItems?.[0]?.dataIndex ?? null;
  if (index === null) {
    return [];
  }
  const invoices = cashConversionSeries.value[index]?.invoices ?? [];
  return invoices.map((invoice) => {
    const txt = invoice?.txt || "";
    const name = invoice?.name || "";
    return name ? `${txt} - ${name}` : txt;
  });
};
const upcomingDue = computed(() => financeSummary.value.upcoming_due || {});
const upcomingDueJobs = computed(() => upcomingDue.value.jobs || []);
const upcomingDueRange = computed(() => {
  if (!upcomingDue.value.start_date || !upcomingDue.value.end_date) {
    return "Next 30 calendar days";
  }

  return `${dayjs(upcomingDue.value.start_date).format("D MMM")} - ${dayjs(
    upcomingDue.value.end_date,
  ).format("D MMM YYYY")}`;
});

const bankMonthlyInBars = computed(() => bankSummary.value.monthly_in || []);
const bankMonthlyOutBars = computed(() => bankSummary.value.monthly_out || []);
const bankRecentInBars = computed(() => bankMonthlyInBars.value.slice(-12));
const bankRecentOutBars = computed(() => bankMonthlyOutBars.value.slice(-12));
const bankYearlyOutBars = computed(() =>
  (bankSummary.value.year_over_year_out || []).map((entry) => ({
    label: entry.label,
    total: entry.total ?? 0,
  })),
);

const totalBankIn = computed(() =>
  bankRecentInBars.value.reduce((sum, entry) => sum + (entry?.total ?? 0), 0),
);
const totalBankOut = computed(() =>
  bankRecentOutBars.value.reduce((sum, entry) => sum + (entry?.total ?? 0), 0),
);
const netBankFlow = computed(() => totalBankIn.value - totalBankOut.value);
const overlayRange = ref(12);
const overlayRangeOptions = [
  { value: 6, label: "Last 6 months" },
  { value: 12, label: "Last 12 months" },
  { value: 24, label: "Last 24 months" },
  { value: 36, label: "Last 3 years" },
  { value: 48, label: "Last 4 years" },
  { value: 60, label: "Last 5 years" },
];
const overlayRangeLabel = computed(
  () => overlayRangeOptions.find((option) => option.value === overlayRange.value)?.label ?? "Range",
);

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(Number(value ?? 0) / 100);
const formatDate = (value) => (value ? dayjs(value).format("D MMM YYYY") : "");

const overlaySeriesFor = (entries) => {
  const current = (entries || []).map((entry) => ({
    label: entry.label,
    total: entry.total ?? 0,
  }));
  const previous = (entries || []).map((entry) => {
    const month = dayjs(`${entry.month}-01`).subtract(1, "year");
    return {
      label: month.format("MMM YY"),
      total: entry.previous_total ?? 0,
    };
  });
  return [...previous, ...current];
};

const overlayIncomingSeries = computed(() =>
  overlaySeriesFor(bankMonthlyInBars.value).slice(-overlayRange.value),
);
const overlayOutgoingSeries = computed(() =>
  overlaySeriesFor(bankMonthlyOutBars.value).slice(-overlayRange.value),
);
const overlayLabels = computed(() =>
  overlayIncomingSeries.value.length
    ? overlayIncomingSeries.value.map((entry) => entry.label)
    : overlayOutgoingSeries.value.map((entry) => entry.label),
);

const netLongSeries = computed(() => {
  const incoming = bankMonthlyInBars.value;
  const outgoing = bankMonthlyOutBars.value;
  const length = Math.min(incoming.length, outgoing.length);
  const incomingSlice = incoming.slice(-length);
  const outgoingSlice = outgoing.slice(-length);
  return incomingSlice.map((entry, index) => ({
    label: entry.label,
    total: (entry.total ?? 0) - (outgoingSlice[index]?.total ?? 0),
  }));
});
const netLongLabels = computed(() => netLongSeries.value.map((entry) => entry.label));
const netLongValues = computed(() => netLongSeries.value.map((entry) => entry.total));

const loadFinanceSummary = async () => {
  loading.value = true;
  error.value = "";
  try {
    const { data } = await apiClient.get("/api/invoice/summary", {
      params: { months: 60 },
    });
    const monthlyTotals = (data?.monthly_totals ?? []).map((entry) => ({
      ...entry,
      previous_total: entry?.previous_total ?? 0,
    }));
    financeSummary.value = {
      monthly_totals: monthlyTotals,
      sales_by_month: data?.sales_by_month ?? [],
      year_over_year: data?.year_over_year ?? [],
      cash_conversion_gap: data?.cash_conversion_gap ?? [],
      upcoming_due: data?.upcoming_due ?? {
        start_date: null,
        end_date: null,
        days: 30,
        count: 0,
        total: 0,
        jobs: [],
      },
    };
  } catch (err) {
    error.value = "Unable to load reports right now.";
  } finally {
    loading.value = false;
  }
};

const loadBankSummary = async () => {
  try {
    const { data } = await apiClient.get("/api/bank-transactions/summary", {
      params: { months: 60 },
    });
    bankSummary.value = {
      monthly_in: data?.monthly_in ?? [],
      monthly_out: data?.monthly_out ?? [],
      year_over_year_out: data?.year_over_year_out ?? [],
    };
  } catch (err) {
    error.value = "Unable to load reports right now.";
  }
};

onMounted(() => {
  loadFinanceSummary();
  loadBankSummary();
});
</script>

<template>
  <section class="p-6 space-y-6">
    <header class="rounded-2xl bg-slate-800 text-white p-6 shadow-lg">
      <div class="flex flex-col gap-2">
        <p class="text-xs uppercase tracking-[0.25em] text-slate-300">Reports</p>
        <h1 class="text-2xl md:text-3xl font-semibold">
          Financial Analytics
        </h1>
        <p class="text-xs text-slate-400">
          Updated {{ dayjs().format("D MMM YYYY, HH:mm") }}
        </p>
      </div>
    </header>

    <div v-if="error" class="text-sm text-rose-600">{{ error }}</div>

    <div class="grid gap-6 md:grid-cols-3">
      <div class="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800">
        <p class="text-xs uppercase tracking-wide text-slate-500">Total cash in</p>
        <p class="mt-2 text-2xl font-semibold text-emerald-600">
          {{ formatCurrency(totalBankIn) }}
        </p>
        <p class="mt-1 text-xs text-slate-400">Last 12 months</p>
      </div>
      <div class="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800">
        <p class="text-xs uppercase tracking-wide text-slate-500">Total cash out</p>
        <p class="mt-2 text-2xl font-semibold text-rose-500">
          {{ formatCurrency(totalBankOut) }}
        </p>
        <p class="mt-1 text-xs text-slate-400">Last 12 months</p>
      </div>
      <div class="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800">
        <p class="text-xs uppercase tracking-wide text-slate-500">Net cashflow</p>
        <p class="mt-2 text-2xl font-semibold" :class="netBankFlow >= 0 ? 'text-emerald-600' : 'text-rose-500'">
          {{ formatCurrency(netBankFlow) }}
        </p>
        <p class="mt-1 text-xs text-slate-400">In minus out</p>
      </div>
    </div>

    <div
      class="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800"
    >
      <div
        class="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between"
      >
        <div>
          <p class="text-xs uppercase tracking-wide text-slate-500">
            Upcoming due
          </p>
          <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
            Unpaid calendar jobs
          </h2>
          <p class="mt-1 text-xs text-slate-400">
            {{ upcomingDueRange }}
          </p>
        </div>
        <div class="text-left lg:text-right">
          <p class="text-3xl font-semibold text-slate-900 dark:text-slate-100">
            {{ formatCurrency(upcomingDue.total) }}
          </p>
          <p class="mt-1 text-xs text-slate-400">
            {{ upcomingDue.count || 0 }} unpaid
            {{ (upcomingDue.count || 0) === 1 ? "job" : "jobs" }}
          </p>
        </div>
      </div>

      <div class="mt-5">
        <div
          v-if="loading"
          class="h-32 animate-pulse rounded-xl bg-slate-100 dark:bg-slate-700"
        ></div>
        <div
          v-else-if="upcomingDueJobs.length"
          class="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700"
        >
          <div
            v-for="job in upcomingDueJobs"
            :key="job.invoice_id"
            class="grid gap-3 border-b border-slate-200 px-4 py-3 text-sm last:border-b-0 dark:border-slate-700 md:grid-cols-[minmax(0,1fr)_9rem_8rem]"
          >
            <div class="min-w-0">
              <RouterLink
                :to="`/invoice/${job.invoice_id}`"
                class="font-medium text-slate-900 hover:text-emerald-700 dark:text-slate-100 dark:hover:text-emerald-300"
              >
                {{ job.reference?.txt }} - {{ job.reference?.name }}
              </RouterLink>
              <p
                v-if="job.reference?.customer"
                class="mt-1 truncate text-xs text-slate-500"
              >
                {{ job.reference.customer }}
              </p>
            </div>
            <div class="text-slate-600 dark:text-slate-300">
              {{ formatDate(job.date) }}
              <p class="text-xs text-slate-400">
                {{
                  job.days_until === 0 ? "Today" : `In ${job.days_until} days`
                }}
              </p>
            </div>
            <div
              class="font-semibold text-slate-900 dark:text-slate-100 md:text-right"
            >
              {{ formatCurrency(job.total) }}
            </div>
          </div>
        </div>
        <p v-else class="text-sm text-slate-500">
          No unpaid calendar jobs due in this period.
        </p>
      </div>
    </div>

    <div class="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <div class="flex items-center justify-between">
        <div>
          <p class="text-xs uppercase tracking-wide text-slate-500">
            Cashflow overlay
          </p>
          <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
            Incoming vs outgoing
          </h2>
        </div>
        <div class="flex items-center gap-3 text-xs text-slate-400">
          <span>{{ overlayRangeLabel }}</span>
          <select v-model.number="overlayRange" class="input text-xs">
            <option v-for="option in overlayRangeOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>
      </div>
      <div class="mt-4">
        <div v-if="loading" class="h-60 animate-pulse rounded-xl bg-slate-100 dark:bg-slate-700"></div>
        <FinanceLineChart
          v-else
          :labels="overlayLabels"
          :incoming="overlayIncomingSeries.map((entry) => entry.total)"
          :outgoing="overlayOutgoingSeries.map((entry) => entry.total)"
        />
      </div>
    </div>

    <div class="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <div class="flex items-center justify-between">
        <div>
          <p class="text-xs uppercase tracking-wide text-slate-500">
            Net cashflow trend
          </p>
          <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
            Rolling 5 years
          </h2>
        </div>
        <span class="text-xs text-slate-400">In minus out</span>
      </div>
      <div class="mt-4">
        <div v-if="loading" class="h-56 animate-pulse rounded-xl bg-slate-100 dark:bg-slate-700"></div>
        <FinanceNetLineChart
          v-else
          :labels="netLongLabels"
          :values="netLongValues"
        />
      </div>
    </div>

    <div class="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <div class="flex items-center justify-between">
        <div>
          <p class="text-xs uppercase tracking-wide text-slate-500">
            Cash conversion gap
          </p>
          <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
            Days from cost to cash
          </h2>
        </div>
        <span class="text-xs text-slate-400">Avg days</span>
      </div>
      <div class="mt-4">
        <div v-if="loading" class="h-56 animate-pulse rounded-xl bg-slate-100 dark:bg-slate-700"></div>
        <div v-else-if="cashConversionValues.length">
          <FinanceNetLineChart
            :labels="cashConversionLabels"
            :values="cashConversionValues"
            label="Cash conversion gap"
            format="days"
            :tooltip-rows="cashConversionTooltipRows"
          />
        </div>
        <p v-else class="text-xs text-slate-500">
          Not enough invoice history yet.
        </p>
      </div>
    </div>

    <div class="grid gap-6 lg:grid-cols-2">
      <div class="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-xs uppercase tracking-wide text-slate-500">
              Last 12 months vs prior year
            </p>
            <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
              Rolling 12-month comparison
            </h2>
          </div>
          <span class="text-xs text-slate-400">Invoices</span>
        </div>
        <div class="mt-4">
          <div v-if="loading" class="h-56 animate-pulse rounded-xl bg-slate-100 dark:bg-slate-700"></div>
          <FinanceBarChart
            v-else
            :bars="monthlyComparisonBars"
            :showLegend="true"
            current-label="Last 12 months"
            previous-label="Prior year"
          />
        </div>
      </div>

      <div class="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-xs uppercase tracking-wide text-slate-500">
              Year-on-year totals
            </p>
            <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
              Fiscal year starting Oct 1
            </h2>
          </div>
          <span class="text-xs text-slate-400">Totals</span>
        </div>
        <div class="mt-4">
          <div v-if="loading" class="h-56 animate-pulse rounded-xl bg-slate-100 dark:bg-slate-700"></div>
          <div v-else-if="financeYearlyBars.length">
            <FinanceBarChart :bars="financeYearlyBars" />
          </div>
          <p v-else class="text-xs text-slate-500">
            Not enough historical invoice data yet.
          </p>
        </div>
      </div>
    </div>

    <div class="grid gap-6 lg:grid-cols-2">
      <div class="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-xs uppercase tracking-wide text-slate-500">
              Sales
            </p>
            <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
              Last 12 months vs prior year
            </h2>
          </div>
          <span class="text-xs text-slate-400">Calendar invoices</span>
        </div>
        <div class="mt-4">
          <div v-if="loading" class="h-56 animate-pulse rounded-xl bg-slate-100 dark:bg-slate-700"></div>
          <FinanceBarChart
            v-else
            :bars="monthlySalesBars"
            :showLegend="true"
            current-label="Last 12 months"
            previous-label="Prior year"
          />
        </div>
      </div>

      <div class="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-xs uppercase tracking-wide text-slate-500">
              Cash in
            </p>
            <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
              Last 12 months vs prior year
            </h2>
          </div>
          <span class="text-xs text-slate-400">Paid in</span>
        </div>
        <div class="mt-4">
          <div v-if="loading" class="h-56 animate-pulse rounded-xl bg-slate-100 dark:bg-slate-700"></div>
          <FinanceBarChart
            v-else
            :bars="bankRecentInBars"
            :showLegend="true"
            current-label="Last 12 months"
            previous-label="Prior year"
          />
        </div>
      </div>

      <div class="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-xs uppercase tracking-wide text-slate-500">
              Cash out
            </p>
            <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
              Year-on-year totals (FY Oct 1)
            </h2>
          </div>
          <span class="text-xs text-slate-400">Paid out</span>
        </div>
        <div class="mt-4">
          <div v-if="loading" class="h-56 animate-pulse rounded-xl bg-slate-100 dark:bg-slate-700"></div>
          <div v-else-if="bankYearlyOutBars.length">
            <FinanceBarChart :bars="bankYearlyOutBars" />
          </div>
          <p v-else class="text-xs text-slate-500">Not enough data yet.</p>
        </div>
      </div>
    </div>
  </section>
</template>
